import { FileSchemaIn, DoctorsV2Api } from "../../../shared/api/generated";
import { ApiClient } from "../../../shared/api/services/ApiClient";
import { Result } from "../../../shared/api/services/result";

export class SasService {
    private static TOKEN_KEY = 'sasToken';
    private static URL_KEY = 'sasUrl';
    private token: string | undefined;
    private sasUrl: string | undefined;
    private readonly doctorsV2Api: DoctorsV2Api;

    constructor() {
        this.token = localStorage.getItem(SasService.TOKEN_KEY) || undefined;
        this.sasUrl = localStorage.getItem(SasService.URL_KEY) || undefined;
        this.doctorsV2Api = new DoctorsV2Api(new ApiClient());
    }

    private async getToken(): Promise<void> {
        try {
            const res = await this.doctorsV2Api.getSasTokenForDoctorByTransferTokenV2({ transferredToken: localStorage.getItem('token')! });
            if (!res.data?.sasToken || !res.data?.sasUrl) {
                throw new Error('Invalid SAS token response');
            }
            this.token = res.data.sasToken;
            this.sasUrl = res.data.sasUrl.split('?')[0];
            localStorage.setItem(SasService.TOKEN_KEY, this.token);
            localStorage.setItem(SasService.URL_KEY, this.sasUrl);
        } catch (error) {
            console.error("Error getting SAS token:", error);
            throw error;
        }

    }

    private getImageDimensions = async (image: File): Promise<{ width: number; height: number }> => {
        return new Promise((resolve) => {
            const img = new Image();
            img.src = URL.createObjectURL(image);
            img.onload = () => {
                const dimensions = { width: img.width, height: img.height };
                URL.revokeObjectURL(img.src);
                resolve(dimensions);
            };
        });
    }

    private compressImage = async (file: File, maxWidth = 1920, quality = 0.9): Promise<Blob> => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.src = URL.createObjectURL(file);

            img.onload = () => {
                URL.revokeObjectURL(img.src);
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;

                if (width > maxWidth) {
                    height = Math.round((height * maxWidth) / width);
                    width = maxWidth;
                }

                canvas.width = width;
                canvas.height = height;

                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    reject(new Error('Failed to get canvas context'));
                    return;
                }

                ctx.drawImage(img, 0, 0, width, height);
                canvas.toBlob(
                    (blob) => {
                        if (blob) {
                            resolve(blob);
                        } else {
                            reject(new Error('Failed to compress image'));
                        }
                    },
                    file.type || 'image/jpeg',
                    quality
                );
            };

            img.onerror = () => reject(new Error('Failed to load image'));
        });
    };

    public uploadImage = async (image: File): Promise<Result<FileSchemaIn>> => {
        try {
            if (!this.sasUrl || !this.token) {
                await this.getToken();
            }

            const compressedBlob = await this.compressImage(image);
            const bytes = await compressedBlob.arrayBuffer();

            const fileName = crypto.randomUUID();
            const extension = image.name.split('.').pop() || 'jpg';
            const url = `${this.sasUrl}/${fileName}.${extension}?${this.token}`;
            const mimeType = image.type || 'image/jpeg';
            const { width, height } = await this.getImageDimensions(new File([compressedBlob], image.name));
            const fileSize = bytes.byteLength;

            const headers = {
                'x-ms-blob-type': 'BlockBlob',
                'Content-Type': mimeType ?? 'image/jpeg',
            };
            const config = {
                method: 'PUT',
                body: bytes,
                headers: headers,
            };
            const imageData: FileSchemaIn = {
                url: `${this.sasUrl}/${fileName}.${extension}`,
                extension: extension,
                fileSize: fileSize,
                height: height,
                width: width,
                contentType: mimeType ?? 'image/jpeg',
                fileName: fileName,
                s3key: `${fileName}.${extension}`,
            };
            const response = await fetch(url, config);
            if (response.status === 201) {
                return Result.success(imageData);
            }
            if (response.status === 404 || response.status === 403) {
                await this.getToken();
                const url = `${this.sasUrl}/${fileName}.${extension}?${this.token}`;
                const retryResult = await fetch(url, config);
                return retryResult.status === 201 ? Result.success(imageData) : Result.failure(`Failed to upload image: ${retryResult.statusText}`);
            }
            return Result.failure(`Failed to upload image: ${response.statusText}`);
        } catch (error) {
            return Result.failure(`Unexpected error during upload: ${error}`);
        }
    }

    public getImage = async (fileUrl: string): Promise<Result<File>> => {
        try {
            if (!this.sasUrl || !this.token) {
                await this.getToken();
            }

            var url = `${fileUrl}?${this.token}`;
            const response = await fetch(url, {
                method: 'GET',
            });
            if (response.status === 200) {
                const body = await response.blob();
                return Result.success(new File([body], fileUrl.split('/').pop()!));
            }
            if (response.status === 404 || response.status === 403) {
                await this.getToken();
                url = `${fileUrl}?${this.token}`;
                const retryResult = await fetch(url, {
                    method: 'GET',
                });
                const body = await retryResult.blob();
                return retryResult.status === 200 ? Result.success(new File([body], fileUrl.split('/').pop()!)) : Result.failure(`Failed to get image: ${retryResult.statusText}`);
            }
            return Result.failure(`Failed to get image: ${response.status}`);
        } catch (error) {
            return Result.failure(`Unexpected error retrieving image: ${error}`);
        }
    }
}